/**
 * PropertiesHelper - Centralized service for building block properties JSONB.
 *
 * This service provides a type-safe interface for constructing properties objects
 * for all block types. It handles validation, defaults, and proper structure for
 * the properties JSONB column.
 *
 * Key responsibilities:
 * - Main dispatcher: buildProperties(type, input) routes to type-specific builders
 * - Type-specific builders for each block type
 * - Spatial property handling (x, y, coordinateSystem)
 * - Layout property construction
 * - Input validation and schema compliance
 *
 * Used during block creation and updates to ensure consistent property structure.
 */

import {
	PlayPropertiesSchema,
	FormationPropertiesSchema,
	ConceptPropertiesSchema,
	DocumentPropertiesSchema,
	PlayerPropertiesSchema,
	DrawingPropertiesSchema,
	ControlPointPropertiesSchema,
	AnnotationPropertiesSchema,
	PreSnapMarkerPropertiesSchema,
	SectionPropertiesSchema,
	TextPropertiesSchema,
	HeaderPropertiesSchema,
	DividerPropertiesSchema,
	ShapePropertiesSchema,
	ImagePropertiesSchema,
	RowPropertiesSchema,
	ColumnPropertiesSchema,
	type PlayProperties,
	type FormationProperties,
	type ConceptProperties,
	type DocumentProperties,
	type DrawingProperties,
	type AnnotationProperties,
	type PreSnapMarkerProperties,
	type SectionProperties,
	type TextProperties,
	type HeaderProperties,
	type DividerProperties,
	type ShapeProperties,
	type ImageProperties,
	type RowProperties,
	type ColumnProperties,
} from '@/types/BlockProperties'
import type { PlayBlockType } from '@/types/blocks/Enums'

// ========== Spatial Properties ==========

/**
 * Coordinate system for spatial positioning.
 * - 'field': Football field coordinates (yards, relative to field dimensions)
 * - 'canvas': Document/slide canvas coordinates (percentage-based)
 */
export type CoordinateSystem = 'field' | 'canvas'

/**
 * Spatial positioning properties for blocks on a canvas.
 * Used for player positions, control points, and canvas content blocks.
 */
export interface SpatialProperties {
	/** X coordinate (interpretation depends on coordinateSystem) */
	x: number
	/** Y coordinate (interpretation depends on coordinateSystem) */
	y: number
	/** Coordinate system used for x/y values */
	coordinateSystem: CoordinateSystem
}

// ========== Input Types for Type-Specific Builders ==========

/**
 * Input for building player properties.
 */
export interface PlayerPropertiesInput {
	label: string
	color: string
	/** Spatial position on the field */
	x: number
	y: number
	coordinateSystem?: CoordinateSystem
	isLineman?: boolean
	isGhost?: boolean
	role?: string
	hashRelative?: boolean
	hashMark?: 'left' | 'middle' | 'right' | null
	leverageShading?: 'none' | 'left' | 'right'
}

/**
 * Input for building control point properties.
 */
export interface ControlPointPropertiesInput {
	pointType: string
	/** Spatial position within the drawing */
	x: number
	y: number
	coordinateSystem?: CoordinateSystem
	isSnapped?: boolean
	/** Embedded bezier handles (replaces separate BezierHandleBlock children) */
	handles?: {
		in?: { x: number; y: number }
		out?: { x: number; y: number }
	}
}

/**
 * Input for building drawing properties.
 */
export interface DrawingPropertiesInput {
	color?: string
	strokeWidth?: number
	brushSize?: number
	lineStyle?: string
	lineEnd?: string
	pathMode?: string
	templateId?: string
	selectorType?: string
	selectorParams?: Record<string, unknown>
}

/**
 * Input for building annotation properties.
 */
export interface AnnotationPropertiesInput {
	text: string
	color?: string
	fontSize?: number
	offsetX?: number
	offsetY?: number
}

/**
 * Input for building shape properties.
 */
export interface ShapePropertiesInput {
	shapeType: string
	strokeColor?: string
	fillColor?: string
	strokeWidth?: number
	x?: number
	y?: number
	width?: number
	height?: number
}

/**
 * Input for building image properties.
 */
export interface ImagePropertiesInput {
	url: string
	alt?: string
	x?: number
	y?: number
	width?: number
	height?: number
}

/**
 * Input for building document properties.
 */
export interface DocumentPropertiesInput {
	name: string
	description?: string
	documentType?: string
	canvasPreset?: string
	layout?: LayoutPropertiesInput
}

/**
 * Input for building section properties.
 */
export interface SectionPropertiesInput {
	name: string
	color?: string
	collapsed?: boolean
	/** Width for spatial sections (percentage) */
	width?: number
	/** Height for spatial sections (percentage) */
	height?: number
	layout?: LayoutPropertiesInput
}

/**
 * Input for building text properties.
 */
export interface TextPropertiesInput {
	content: string
	alignment?: string
	fontSize?: number
	color?: string
	// Spatial properties for presentation/document content blocks
	x?: number
	y?: number
	width?: number
	height?: number
}

/**
 * Input for building play properties.
 */
export interface PlayPropertiesInput {
	name: string
	hashPosition?: string
	notes?: string
	displaySettings?: {
		showLinemanLabels?: boolean
		showOffenseOutline?: boolean
		showDefenseOutline?: boolean
	}
}

/**
 * Layout properties input structure.
 */
export interface LayoutPropertiesInput {
	mode?: string
	gap?: number
	padding?: number
	alignMain?: string
	alignCross?: string
	wrap?: boolean
	gridColumns?: number
	gridRows?: number
}

// ========== Type-Specific Property Builders ==========

/**
 * Build properties for a player block.
 * Includes spatial positioning (x, y, coordinateSystem).
 */
export function buildPlayerProperties(input: PlayerPropertiesInput): Record<string, unknown> {
	const baseProperties = PlayerPropertiesSchema.parse({
		label: input.label,
		color: input.color,
		isLineman: input.isLineman ?? false,
		isGhost: input.isGhost ?? false,
		role: input.role,
		hashRelative: input.hashRelative ?? false,
		hashMark: input.hashMark ?? null,
		leverageShading: input.leverageShading ?? 'none',
	})

	return {
		...baseProperties,
		x: input.x,
		y: input.y,
		coordinateSystem: (input.coordinateSystem ?? 'field') as CoordinateSystem,
	}
}

/**
 * Build properties for a control point block.
 * Includes spatial positioning (x, y, coordinateSystem) and embedded handles.
 */
export function buildControlPointProperties(
	input: ControlPointPropertiesInput
): Record<string, unknown> {
	const baseProperties = ControlPointPropertiesSchema.parse({
		pointType: input.pointType,
		isSnapped: input.isSnapped ?? false,
		handles: input.handles,
	})

	return {
		...baseProperties,
		x: input.x,
		y: input.y,
		coordinateSystem: (input.coordinateSystem ?? 'field') as CoordinateSystem,
	}
}

/**
 * Build properties for a drawing block.
 * Handles line style, color, and path configuration.
 */
export function buildDrawingProperties(input: DrawingPropertiesInput): DrawingProperties {
	const style =
		input.color || input.strokeWidth || input.lineStyle || input.lineEnd || input.pathMode
			? {
					color: input.color,
					strokeWidth: input.strokeWidth ?? 2,
					brushSize: input.brushSize,
					lineStyle: input.lineStyle ?? 'solid',
					lineEnd: input.lineEnd ?? 'arrow',
					pathMode: input.pathMode ?? 'curve',
			  }
			: undefined

	return DrawingPropertiesSchema.parse({
		style,
		templateId: input.templateId,
		selectorType: input.selectorType,
		selectorParams: input.selectorParams,
	})
}

/**
 * Build properties for an annotation block.
 * Text labels on the canvas with offset positioning.
 */
export function buildAnnotationProperties(input: AnnotationPropertiesInput): AnnotationProperties {
	return AnnotationPropertiesSchema.parse({
		text: input.text,
		color: input.color,
		fontSize: input.fontSize,
		offsetX: input.offsetX ?? 0,
		offsetY: input.offsetY ?? 0,
	})
}

/**
 * Build properties for a document block.
 * Includes layout configuration for child positioning.
 */
export function buildDocumentProperties(input: DocumentPropertiesInput): DocumentProperties {
	return DocumentPropertiesSchema.parse({
		name: input.name,
		description: input.description,
		documentType: input.documentType ?? 'call_sheet',
		canvasPreset: input.canvasPreset ?? 'letter',
		layout: input.layout ? buildLayoutPropertiesObject(input.layout) : undefined,
	})
}

/**
 * Build properties for a section block.
 * Includes layout configuration and optional spatial dimensions.
 */
export function buildSectionProperties(input: SectionPropertiesInput): SectionProperties {
	const baseProperties = SectionPropertiesSchema.parse({
		name: input.name,
		color: input.color,
		collapsed: input.collapsed ?? false,
		layout: input.layout ? buildLayoutPropertiesObject(input.layout) : undefined,
	})

	// Add spatial dimensions if provided
	if (input.width !== undefined || input.height !== undefined) {
		return {
			...baseProperties,
			width: input.width,
			height: input.height,
		} as SectionProperties
	}

	return baseProperties
}

/**
 * Build properties for a text block.
 * Content, alignment, and styling.
 */
export function buildTextProperties(input: TextPropertiesInput): TextProperties {
	const props: any = {
		content: input.content,
		alignment: input.alignment ?? 'left',
		fontSize: input.fontSize,
		color: input.color,
	}
	// Add spatial properties if provided (for presentation/document content blocks)
	if (input.x !== undefined) props.x = input.x
	if (input.y !== undefined) props.y = input.y
	if (input.width !== undefined) props.width = input.width
	if (input.height !== undefined) props.height = input.height

	return TextPropertiesSchema.parse(props)
}

/**
 * Build properties for a play block.
 * Name, hash position, notes, and display settings.
 */
export function buildPlayProperties(input: PlayPropertiesInput): PlayProperties {
	return PlayPropertiesSchema.parse({
		name: input.name,
		hashPosition: input.hashPosition ?? 'middle',
		notes: input.notes,
		displaySettings: input.displaySettings,
	})
}

/**
 * Build properties for a formation block.
 */
export function buildFormationProperties(input: {
	name: string
	description?: string
	hashMode?: boolean
	thumbnail?: string
}): FormationProperties {
	return FormationPropertiesSchema.parse({
		name: input.name,
		description: input.description,
		hashMode: input.hashMode ?? false,
		thumbnail: input.thumbnail,
	})
}

/**
 * Build properties for a concept block.
 */
export function buildConceptProperties(input: {
	name: string
	description?: string
	targetingMode?: string
	ballPosition?: string
	playDirection?: string
}): ConceptProperties {
	return ConceptPropertiesSchema.parse({
		name: input.name,
		description: input.description,
		targetingMode: input.targetingMode ?? 'absolute_role',
		ballPosition: input.ballPosition ?? 'center',
		playDirection: input.playDirection ?? 'na',
	})
}

/**
 * Build properties for a pre-snap marker block.
 */
export function buildPreSnapMarkerProperties(input: {
	motionType: string
	snapPointId?: number
}): PreSnapMarkerProperties {
	return PreSnapMarkerPropertiesSchema.parse({
		motionType: input.motionType,
		snapPointId: input.snapPointId,
	})
}

/**
 * Build properties for a header block.
 */
export function buildHeaderProperties(input: { level: number; text: string }): HeaderProperties {
	return HeaderPropertiesSchema.parse({
		level: input.level,
		text: input.text,
	})
}

/**
 * Build properties for a divider block.
 */
export function buildDividerProperties(input: {
	style?: string
	thickness?: number
	color?: string
}): DividerProperties {
	return DividerPropertiesSchema.parse({
		style: input.style ?? 'solid',
		thickness: input.thickness ?? 1,
		color: input.color,
	})
}

/**
 * Build properties for a shape block.
 * Includes optional spatial positioning for slides/documents.
 */
export function buildShapeProperties(input: ShapePropertiesInput): ShapeProperties {
	const props: Record<string, unknown> = {
		shapeType: input.shapeType,
		strokeColor: input.strokeColor,
		fillColor: input.fillColor,
		strokeWidth: input.strokeWidth,
	}
	if (input.x !== undefined) props.x = input.x
	if (input.y !== undefined) props.y = input.y
	if (input.width !== undefined) props.width = input.width
	if (input.height !== undefined) props.height = input.height

	return ShapePropertiesSchema.parse(props)
}

/**
 * Build properties for an image block.
 * Includes optional spatial positioning for slides/documents.
 */
export function buildImageProperties(input: ImagePropertiesInput): ImageProperties {
	const props: Record<string, unknown> = {
		url: input.url,
		alt: input.alt,
	}
	if (input.x !== undefined) props.x = input.x
	if (input.y !== undefined) props.y = input.y
	if (input.width !== undefined) props.width = input.width
	if (input.height !== undefined) props.height = input.height

	return ImagePropertiesSchema.parse(props)
}

/**
 * Build properties for a row block.
 */
export function buildRowProperties(input: { layout?: LayoutPropertiesInput }): RowProperties {
	return RowPropertiesSchema.parse({
		layout: input.layout ? buildLayoutPropertiesObject(input.layout) : undefined,
	})
}

/**
 * Build properties for a column block.
 */
export function buildColumnProperties(input: { layout?: LayoutPropertiesInput }): ColumnProperties {
	return ColumnPropertiesSchema.parse({
		layout: input.layout ? buildLayoutPropertiesObject(input.layout) : undefined,
	})
}

// ========== Layout Properties Builder ==========

/**
 * Build layout properties object for container blocks.
 * Converts input to validated layout configuration.
 */
export function buildLayoutPropertiesObject(input: LayoutPropertiesInput): Record<string, unknown> {
	const layout: Record<string, unknown> = {}

	if (input.mode !== undefined) layout.mode = input.mode
	if (input.gap !== undefined) layout.gap = input.gap
	if (input.padding !== undefined) layout.padding = input.padding
	if (input.alignMain !== undefined) layout.alignMain = input.alignMain
	if (input.alignCross !== undefined) layout.alignCross = input.alignCross
	if (input.wrap !== undefined) layout.wrap = input.wrap
	if (input.gridColumns !== undefined) layout.gridColumns = input.gridColumns
	if (input.gridRows !== undefined) layout.gridRows = input.gridRows

	return Object.keys(layout).length > 0 ? layout : {}
}

// ========== Main Dispatcher ==========

/**
 * Union type of all possible property input types.
 */
export type PropertiesInput =
	| PlayerPropertiesInput
	| ControlPointPropertiesInput
	| DrawingPropertiesInput
	| AnnotationPropertiesInput
	| DocumentPropertiesInput
	| SectionPropertiesInput
	| TextPropertiesInput
	| PlayPropertiesInput
	| ShapePropertiesInput
	| ImagePropertiesInput
	| { name: string; description?: string; hashMode?: boolean; thumbnail?: string } // Formation
	| {
			name: string
			description?: string
			targetingMode?: string
			ballPosition?: string
			playDirection?: string
	  } // Concept
	| { motionType: string; snapPointId?: number } // PreSnapMarker
	| { level: number; text: string } // Header
	| { style?: string; thickness?: number; color?: string } // Divider
	| { layout?: LayoutPropertiesInput } // Row, Column

/**
 * Main dispatcher function for building block properties.
 * Routes to the appropriate type-specific builder based on type.
 *
 * @param blockType - The type of block to build properties for
 * @param input - Type-specific input data
 * @returns Validated and structured properties object
 *
 * @example
 * ```ts
 * const playerProps = buildProperties('player', {
 *   label: 'QB',
 *   color: '#FF0000',
 *   x: 50,
 *   y: 50,
 *   coordinateSystem: 'field'
 * })
 * ```
 */
export function buildProperties(blockType: PlayBlockType, input: PropertiesInput): Record<string, unknown> {
	switch (blockType) {
		// Root blocks
		case 'play':
			return buildPlayProperties(input as PlayPropertiesInput)
		case 'formation':
			return buildFormationProperties(input as { name: string; description?: string; hashMode?: boolean; thumbnail?: string })
		case 'concept':
			return buildConceptProperties(
				input as {
					name: string
					description?: string
					targetingMode?: string
					ballPosition?: string
					playDirection?: string
				}
			)
		case 'document':
			return buildDocumentProperties(input as DocumentPropertiesInput)

		// Canvas elements
		case 'player':
			return buildPlayerProperties(input as PlayerPropertiesInput)
		case 'drawing':
			return buildDrawingProperties(input as DrawingPropertiesInput)
		case 'control_point':
			return buildControlPointProperties(input as ControlPointPropertiesInput)
		case 'annotation':
			return buildAnnotationProperties(input as AnnotationPropertiesInput)
		case 'pre_snap_marker':
			return buildPreSnapMarkerProperties(input as { motionType: string; snapPointId?: number })

		// Content blocks
		case 'section':
			return buildSectionProperties(input as SectionPropertiesInput)
		case 'text':
			return buildTextProperties(input as TextPropertiesInput)
		case 'header':
			return buildHeaderProperties(input as { level: number; text: string })
		case 'divider':
			return buildDividerProperties(
				input as { style?: string; thickness?: number; color?: string }
			)
		case 'shape':
			return buildShapeProperties(input as ShapePropertiesInput)
		case 'image':
			return buildImageProperties(input as ImagePropertiesInput)

		// Layout containers
		case 'row':
			return buildRowProperties(input as { layout?: LayoutPropertiesInput })
		case 'column':
			return buildColumnProperties(input as { layout?: LayoutPropertiesInput })

		// Reference blocks (no properties)
		case 'play_reference':
		case 'formation_reference':
		case 'document_reference':
		case 'concept_reference':
			return {}

		default:
			throw new Error(`Unknown block type: ${blockType}`)
	}
}

/**
 * Type guard to check if properties include spatial data.
 */
export function hasSpatialProperties(
	properties: Record<string, unknown>
): properties is Record<string, unknown> & SpatialProperties {
	return (
		typeof properties.x === 'number' &&
		typeof properties.y === 'number' &&
		(properties.coordinateSystem === 'field' || properties.coordinateSystem === 'canvas')
	)
}

/**
 * Extract spatial properties from a properties object.
 * Returns null if the properties don't include spatial data.
 */
export function extractSpatialProperties(
	properties: Record<string, unknown>
): SpatialProperties | null {
	if (!hasSpatialProperties(properties)) {
		return null
	}

	return {
		x: properties.x,
		y: properties.y,
		coordinateSystem: properties.coordinateSystem,
	}
}
